import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { addMinutes, format } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  usePatientSearchForQuickStart,
  type PatientSearchOption,
  QUICK_START_PATIENT_SEARCH_KEY,
} from "@/hooks/usePatientSearchForQuickStart";
import { useCreateAppointment } from "@/hooks/useAppointments";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Play, Plus } from "lucide-react";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { useToast } from "@/hooks/use-toast";
import { generateUUID } from "@/lib/utils/uuid";
import { applyDocumentMask, type DocumentType } from "@/lib/utils/document-masks";
import { highlightSearchTerm } from "@/lib/utils/highlightSearchTerm";
import {
  clearAttendanceDraft,
  readAttendanceDraft,
  writeAttendanceDraft,
  type AttendanceDraft,
  type AttendanceSource,
} from "@/lib/attendance/session-storage";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@akuna/ui";
import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { QuickRegisterPatientDialog } from "@/components/calendar/QuickRegisterPatientDialog";
import { Badge } from "@/components/ui/badge";
import { isPetDeceased } from "@/lib/utils/pet-death";
import { renderIconBySlug } from "@/lib/event-icons";
import { useClinicAppointmentTypes } from "@/hooks/useClinicAppointmentTypes";

type QuickStartAttendanceDialogProps = {
  clinicId: string;
  source: AttendanceSource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type DialogStep = "choose" | "resume" | "finish";

type QuickStartAttendanceInput = {
  ownerId: string;
  petId: string;
  type: string;
};

type ResumeDraftDetails = {
  ownerName: string | null;
  petName: string | null;
};

const CPF_DOCUMENT_TYPE: DocumentType = { id: "cpf", name: "CPF", mask: "000.000.000-00" };
const CNPJ_DOCUMENT_TYPE: DocumentType = { id: "cnpj", name: "CNPJ", mask: "00.000.000/0000-00" };
const RG_DOCUMENT_TYPE: DocumentType = { id: "rg", name: "RG", mask: "00.000.000-0" };

const formatDocumentForDisplay = (document: string | null) => {
  if (!document) return null;
  const digits = document.replace(/\D/g, "");
  if (digits.length === 9) return applyDocumentMask(digits, RG_DOCUMENT_TYPE);
  if (digits.length === 11) return applyDocumentMask(digits, CPF_DOCUMENT_TYPE);
  if (digits.length === 14) return applyDocumentMask(digits, CNPJ_DOCUMENT_TYPE);
  return applyDocumentMask(document);
};

const buildContactSummaryFromOption = (option: PatientSearchOption) => {
  const formattedDocument = formatDocumentForDisplay(option.ownerDocument);
  const parts = [
    formattedDocument,
    option.ownerPrimaryPhone,
    option.ownerPrimaryEmail,
    option.ownerPrimaryAddress,
  ].filter((value): value is string => Boolean(value && value.trim()));
  return parts.slice(0, 4).join(" · ");
};

export function QuickStartAttendanceDialog({
  clinicId,
  source,
  open,
  onOpenChange,
}: QuickStartAttendanceDialogProps) {
  const { t, i18n } = useTranslation(["calendar", "common"]);
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: customerId } = useCurrentClinicCustomerId();
  const createAppointment = useCreateAppointment();
  const locale = i18n.language.startsWith("en") ? enUS : ptBR;

  const [step, setStep] = useState<DialogStep>("choose");
  const [loadingSession, setLoadingSession] = useState(false);

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AttendanceDraft | null>(null);

  const [patientSearch, setPatientSearch] = useState("");
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("");
  const [selectedOwnerName, setSelectedOwnerName] = useState<string>("");
  const [selectedPetId, setSelectedPetId] = useState<string>("");
  const [selectedPetName, setSelectedPetName] = useState<string>("");
  const [selectedPetStatusName, setSelectedPetStatusName] = useState<string | null>(null);
  const [selectedPetDeathDate, setSelectedPetDeathDate] = useState<string | null>(null);
  const { data: clinicTypes = [] } = useClinicAppointmentTypes();
  const [chosenType, setChosenType] = useState<string>("");
  const defaultTypeId = (clinicTypes.find((item) => item.behavior === "padrao") ?? clinicTypes[0])?.id ?? "";
  const selectedType = chosenType || defaultTypeId;
  const setSelectedType = setChosenType;
  const [pendingStartInput, setPendingStartInput] = useState<QuickStartAttendanceInput | null>(null);
  const [isDeceasedConfirmOpen, setIsDeceasedConfirmOpen] = useState(false);
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
  const [registerMode, setRegisterMode] = useState<"owner_and_pet" | "pet_only">("owner_and_pet");

  const debouncedPatientSearch = useDebouncedValue(patientSearch.trim(), 300);
  const { data: patientOptions = [], isLoading: isLoadingPatients } = usePatientSearchForQuickStart(
    debouncedPatientSearch,
  );

  const { data: resumeDetails, isLoading: isLoadingResumeDetails } = useQuery<ResumeDraftDetails>({
    queryKey: ["quick-start-resume-details", draft?.ownerId, draft?.petId, draft?.ownerName, draft?.petName],
    enabled: Boolean(draft?.ownerId && draft?.petId),
    queryFn: async () => {
      if (!draft?.ownerId || !draft?.petId) {
        return { ownerName: null, petName: null };
      }

      if (draft.ownerName && draft.petName) {
        return {
          ownerName: draft.ownerName,
          petName: draft.petName,
        };
      }

      const [ownerResult, petResult] = await Promise.all([
        supabase
          .from("owners")
          .select("name")
          .eq("id", draft.ownerId)
          .maybeSingle(),
        supabase
          .from("pets")
          .select("name")
          .eq("id", draft.petId)
          .maybeSingle(),
      ]);

      if (ownerResult.error) throw ownerResult.error;
      if (petResult.error) throw petResult.error;

      return {
        ownerName: ownerResult.data?.name ?? null,
        petName: petResult.data?.name ?? null,
      };
    },
  });

  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const loadSession = async () => {
      setLoadingSession(true);
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;
        const userId = data.user?.id ?? null;
        if (!isMounted) return;

        setCurrentUserId(userId);

        if (!userId) {
          setDraft(null);
          setStep("choose");
          return;
        }

        const existingDraft = readAttendanceDraft(clinicId, userId);
        if (existingDraft) {
          setDraft(existingDraft);
          setStep("resume");
          return;
        }

        setDraft(null);
        setStep("choose");
      } catch {
        if (!isMounted) return;
        setDraft(null);
        setStep("choose");
      } finally {
        if (isMounted) {
          setLoadingSession(false);
        }
      }
    };

    void loadSession();

    return () => {
      isMounted = false;
    };
  }, [clinicId, open]);

  const resetDialogState = () => {
    setStep("choose");
    setPatientSearch("");
    setSelectedOwnerId("");
    setSelectedOwnerName("");
    setSelectedPetId("");
    setSelectedPetName("");
    setSelectedPetStatusName(null);
    setSelectedPetDeathDate(null);
    setSelectedType("");
    setPendingStartInput(null);
    setIsDeceasedConfirmOpen(false);
    setDraft(null);
    setLoadingSession(false);
    setIsRegisterDialogOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetDialogState();
    }
    onOpenChange(nextOpen);
  };

  const performStartAttendance = async (input: QuickStartAttendanceInput) => {
    const startedAt = new Date();
    const appointment = await createAppointment.mutateAsync({
      clinic_id: clinicId,
      owner_id: input.ownerId,
      pet_ids: [input.petId],
      type: input.type,
      starts_at: startedAt.toISOString(),
      ends_at: addMinutes(startedAt, 1).toISOString(),
      status: "em_atendimento",
    });

    const nextDraft: AttendanceDraft = {
      id: generateUUID(),
      appointmentId: appointment.id,
      clinicId,
      ownerId: input.ownerId,
      ownerName: selectedOwnerName || undefined,
      petId: input.petId,
      petName: selectedPetName || undefined,
      type: input.type,
      startedAtIso: startedAt.toISOString(),
      source,
      createdByUserId: currentUserId,
    };

    writeAttendanceDraft(nextDraft);
    setDraft(nextDraft);
    goToMedicalRecord(nextDraft);
  };

  const handleSelectPatient = (option: PatientSearchOption) => {
    setSelectedOwnerId(option.ownerId);
    setSelectedOwnerName(option.ownerName);
    setSelectedPetId(option.petId);
    setSelectedPetName(option.petName);
    setSelectedPetStatusName(option.petStatusName);
    setSelectedPetDeathDate(option.petDeathDate);
    setPatientSearch(option.label);
  };

  const goToMedicalRecord = (activeDraft: AttendanceDraft) => {
    if (!activeDraft.appointmentId) {
      toast({
        title: t("quickStart.invalidAttendanceTitle"),
        description: t("quickStart.invalidAttendanceDescription"),
        variant: "destructive",
      });
      return;
    }

    handleOpenChange(false);
    navigate(`/patients/pets/${activeDraft.petId}/medical-records/new`, {
      state: {
        appointmentId: activeDraft.appointmentId,
        quickStartAttendance: true,
        quickStartSource: activeDraft.source,
      },
    });
  };

  const handleStartAttendance = async () => {
    if (!currentUserId) {
      toast({
        title: t("quickStart.invalidSessionTitle"),
        description: t("quickStart.invalidSessionDescription"),
        variant: "destructive",
      });
      return;
    }

    const input: QuickStartAttendanceInput = {
      ownerId: selectedOwnerId,
      petId: selectedPetId,
      type: selectedType,
    };

    if (!input.ownerId || !input.petId || !input.type) {
      toast({
        title: t("quickStart.requiredFieldsTitle"),
        description: t("quickStart.requiredFieldsDescription"),
        variant: "destructive",
      });
      return;
    }

    if (isPetDeceased({ status: { name: selectedPetStatusName }, death_date: selectedPetDeathDate })) {
      setPendingStartInput(input);
      setIsDeceasedConfirmOpen(true);
      return;
    }

    try {
      await performStartAttendance(input);
    } catch (error) {
      toast({
        title: t("quickStart.startErrorTitle"),
        description: error instanceof Error ? error.message : t("quickStart.tryAgain"),
        variant: "destructive",
      });
    }
  };

  const handleResumeDraft = () => {
    if (!draft) return;
    goToMedicalRecord(draft);
  };

  const handleReplaceDraft = () => {
    if (currentUserId) {
      clearAttendanceDraft(clinicId, currentUserId);
    }
    setDraft(null);
    setStep("choose");
  };

  const isStarting = createAppointment.isPending;

  const handleOpenRegisterNewPatient = () => {
    setRegisterMode("owner_and_pet");
    setIsRegisterDialogOpen(true);
  };

  const selectedCompositeValue =
    selectedOwnerId && selectedPetId ? `${selectedOwnerId}:${selectedPetId}` : "";

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={handleOpenChange}
      noScrollViewport
      className="flex max-w-xl flex-col p-0 sm:!overflow-visible"
    >
        <div className="shrink-0 border-b border-border/60 px-6 py-4">
          <DialogHeader className="pr-6 text-left">
            <DialogTitle>{t("quickStart.dialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("quickStart.dialogDescription")}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4 sm:overflow-visible">
        {loadingSession ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-5 w-3/5" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : null}

        {!loadingSession && step === "resume" && draft ? (
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <p className="font-medium text-foreground">{t("quickStart.resumeCardTitle")}</p>
              <p className="mt-1 text-muted-foreground">
                {t("quickStart.owner")}:{" "}
                {isLoadingResumeDetails ? t("common:loading") : (resumeDetails?.ownerName ?? t("quickStart.unidentified"))}
              </p>
              <p className="mt-1 text-muted-foreground">
                {t("quickStart.patient")}:{" "}
                {isLoadingResumeDetails ? t("common:loading") : (resumeDetails?.petName ?? t("quickStart.unidentified"))}
              </p>
              <p className="mt-1 text-muted-foreground">
                {t("quickStart.startedAt", { date: format(new Date(draft.startedAtIso), "Pp", { locale }) })}
              </p>
              <p className="mt-1 text-muted-foreground">
                {t("quickStart.attendanceType")}: {clinicTypes.find((item) => item.id === draft.type)?.name ?? ""}
              </p>
            </div>
          </div>
        ) : null}

        {!loadingSession && step === "choose" ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="quick-patient">{t("quickStart.patientLabel")} <span className="text-destructive">*</span></Label>
              <Autocomplete
                value={patientSearch}
                onChange={(value) => {
                  setPatientSearch(value);
                  if (!value.trim()) {
                    setSelectedOwnerId("");
                    setSelectedOwnerName("");
                    setSelectedPetId("");
                    setSelectedPetName("");
                    setSelectedPetStatusName(null);
                    setSelectedPetDeathDate(null);
                    return;
                  }
                  const matched = patientOptions.find(
                    (opt) => opt.label.toLowerCase().trim() === value.toLowerCase().trim(),
                  );
                  if (matched) {
                    handleSelectPatient(matched);
                  } else {
                    setSelectedOwnerId("");
                    setSelectedOwnerName("");
                    setSelectedPetId("");
                    setSelectedPetName("");
                    setSelectedPetStatusName(null);
                    setSelectedPetDeathDate(null);
                  }
                }}
                popoverPortalled={false}
                useInlineDropdown
                onSelect={(option) => handleSelectPatient(option as PatientSearchOption)}
                options={patientOptions}
                selectedValue={selectedCompositeValue}
                disableLocalFilter
                placeholder={t("quickStart.patientPlaceholder")}
                emptyMessage={
                  isLoadingPatients ? t("common:loading") : t("common:noResults")
                }
                footer={
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="w-full justify-start"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenRegisterNewPatient();
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    {t("quickStart.registerPatient")}
                  </Button>
                }
                listClassName="max-h-[280px]"
                optionClassName="group flex items-start gap-3 py-2"
                renderOption={(option, isSelected) => {
                  const opt = option as PatientSearchOption;
                  const term = debouncedPatientSearch;
                  const contactSummary = buildContactSummaryFromOption(opt);
                  return (
                    <div className="flex w-full min-w-0 items-start gap-3">
                      <Avatar className="h-10 w-10 shrink-0">
                        {opt.petPhotoPath ? (
                          <AvatarImage src={opt.petPhotoPath} alt={opt.petName} />
                        ) : null}
                        <AvatarFallback className="bg-muted/50 text-muted-foreground">
                          <CustomIcons name="paws" className="h-5 w-5 text-current" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <p className="text-sm font-medium text-foreground group-hover:text-primary">
                          {term ? highlightSearchTerm(opt.petName, term) : opt.petName}
                          {opt.petRecordNumber ? (
                            <span className="ml-1.5 text-muted-foreground group-hover:text-primary">#{opt.petRecordNumber}</span>
                          ) : null}
                          {isPetDeceased({ status: { name: opt.petStatusName }, death_date: opt.petDeathDate }) ? (
                            <Badge
                              variant="outline"
                              className="ml-2 "
                            >
                              {opt.petStatusName}
                            </Badge>
                          ) : null}
                        </p>
                        <p className="text-xs text-muted-foreground group-hover:text-primary">
                          {t("quickStart.owner")}: {term ? highlightSearchTerm(opt.ownerName, term) : opt.ownerName}
                        </p>
                        {contactSummary ? (
                          <p className="text-xs text-muted-foreground group-hover:text-primary">
                            {contactSummary}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  );
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-type">{t("quickStart.typeLabel")} <span className="text-destructive">*</span></Label>
              <Select value={selectedType} onValueChange={(value) => setSelectedType(value)}>
                <SelectTrigger id="quick-type">
                  <SelectValue placeholder={t("quickStart.typePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {clinicTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <span className="flex items-center gap-2">
                        {renderIconBySlug(type.icon, { sizeClass: "h-4 w-4", tone: "current" })}
                        <span style={{ color: type.color }}>■</span>
                        {type.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

          </div>
        ) : null}
        </div>

        {!loadingSession && step === "resume" && draft ? (
          <div className="flex shrink-0 justify-end gap-2 border-t border-border/60 px-6 py-4">
            <Button variant="outline" onClick={handleReplaceDraft}>
              {t("quickStart.startNew")}
            </Button>
            <Button onClick={handleResumeDraft}>{t("quickStart.resume")}</Button>
          </div>
        ) : null}

        {!loadingSession && step === "choose" ? (
          <div className="flex shrink-0 justify-end gap-2 border-t border-border/60 px-6 py-4">
            <Button
              onClick={handleStartAttendance}
              disabled={!selectedOwnerId || !selectedPetId || isStarting}
              className="gap-2"
            >
              <Play className="h-4 w-4" />
              {isStarting ? t("quickStart.starting") : t("startAttendance")}
            </Button>
          </div>
        ) : null}
        <AlertDialog open={isDeceasedConfirmOpen} onOpenChange={setIsDeceasedConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("quickStart.deceasedSubmitTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("quickStart.deceasedSubmitDescription")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setPendingStartInput(null)}>
                {t("common:cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const pending = pendingStartInput;
                  setPendingStartInput(null);
                  setIsDeceasedConfirmOpen(false);
                  if (pending) {
                    void performStartAttendance(pending).catch((error) => {
                      toast({
                        title: t("quickStart.startErrorTitle"),
                        description: error instanceof Error ? error.message : t("quickStart.tryAgain"),
                        variant: "destructive",
                      });
                    });
                  }
                }}
              >
                {t("startAttendance")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <QuickRegisterPatientDialog
          open={isRegisterDialogOpen}
          onOpenChange={setIsRegisterDialogOpen}
          mode={registerMode}
          initialOwnerId={registerMode === "pet_only" ? selectedOwnerId : undefined}
          initialOwnerName={registerMode === "pet_only" ? selectedOwnerName : undefined}
          onCompleted={async ({ ownerId, ownerName, petId, petName }) => {
            await queryClient.invalidateQueries({ queryKey: [QUICK_START_PATIENT_SEARCH_KEY] });
            setSelectedOwnerId(ownerId);
            setSelectedOwnerName(ownerName);
            setSelectedPetId(petId);
            setSelectedPetName(petName);
            setPatientSearch(`${petName} – ${ownerName}`);
            setIsRegisterDialogOpen(false);
          }}
        />
    </ResponsiveDialog>
  );
}
